import React, { useState, useEffect, useRef } from 'react';
import { BookText, ChevronDown, Calendar, Edit, Save, X, CheckCircle, Trash2, Edit2 } from 'lucide-react';
import { cn } from '../utils/cn';
import { supabase } from '../lib/supabase';
import { Button } from './Button';
import { format, addMonths, addWeeks } from 'date-fns';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';

type VisionSectionProps = {
  open: boolean;
  toggle: (key: string) => void;
  sectionKey: string;
};

export default function VisionSection({ open, toggle, sectionKey }: VisionSectionProps) {
  const [professionalVision, setProfessionalVision] = useState('');
  const [personalVision, setPersonalVision] = useState('');
  const [idealDay, setIdealDay] = useState('');
  const [healthVision, setHealthVision] = useState('');
  const [relationshipsVision, setRelationshipsVision] = useState('');
  const [editingProfessional, setEditingProfessional] = useState(false);
  const [editingPersonal, setEditingPersonal] = useState(false);
  const [editingIdealDay, setEditingIdealDay] = useState(false);
  const [editingHealth, setEditingHealth] = useState(false);
  const [editingRelationships, setEditingRelationships] = useState(false);
  const [professionalFrequency, setProfessionalFrequency] = useState('monthly');
  const [personalFrequency, setPersonalFrequency] = useState('monthly');
  const [idealDayFrequency, setIdealDayFrequency] = useState('monthly');
  const [healthFrequency, setHealthFrequency] = useState('monthly');
  const [relationshipsFrequency, setRelationshipsFrequency] = useState('monthly');
  const [professionalNextReview, setProfessionalNextReview] = useState<Date | null>(null);
  const [personalNextReview, setPersonalNextReview] = useState<Date | null>(null);
  const [idealDayNextReview, setIdealDayNextReview] = useState<Date | null>(null);
  const [healthNextReview, setHealthNextReview] = useState<Date | null>(null);
  const [relationshipsNextReview, setRelationshipsNextReview] = useState<Date | null>(null);
  const [bucketListFrequency, setBucketListFrequency] = useState('monthly');
  const [bucketListNextReview, setBucketListNextReview] = useState<Date | null>(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completingItem, setCompletingItem] = useState<any>(null);
  const [completionStory, setCompletionStory] = useState('');
  const [bucketListItems, setBucketListItems] = useState<any[]>([]);
  const [newBucketItem, setNewBucketItem] = useState('');
  const [newBucketReason, setNewBucketReason] = useState('');
  const [editingBucketItem, setEditingBucketItem] = useState<string | null>(null);
  const [editingBucketText, setEditingBucketText] = useState('');
  const [editingBucketReason, setEditingBucketReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [userTimeZone, setUserTimeZone] = useState('');
  
  // Sub-accordion state
  const [professionalOpen, setProfessionalOpen] = useState(false);
  const [personalOpen, setPersonalOpen] = useState(false);
  const [idealDayOpen, setIdealDayOpen] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [relationshipsOpen, setRelationshipsOpen] = useState(false);
  const [bucketListOpen, setBucketListOpen] = useState(false);
  
  // Bucket list form state
  const [newItem, setNewItem] = useState('');
  const [newReason, setNewReason] = useState('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState('');
  const [editingReason, setEditingReason] = useState('');

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'header': [1, 2, 3, false] }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link'
  ];

  useEffect(() => {
    const fetchVisionData = async () => {
      try {
        setIsLoading(true);
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;

        const { data, error } = await supabase
          .from('profiles')
          .select(`
            professional_vision,
            personal_vision,
            ideal_day,
            health_vision,
            relationships_vision,
            professional_vision_frequency,
            personal_vision_frequency,
            ideal_day_frequency,
            health_vision_frequency,
            relationships_vision_frequency,
            professional_vision_next_review,
            personal_vision_next_review,
            ideal_day_next_review,
            health_vision_next_review,
            relationships_vision_next_review,
            bucket_list_frequency,
            bucket_list_next_review,
            time_zone
          `)
          .eq('id', session.user.id)
          .single();

        if (error) throw error;

        if (data) {
          setProfessionalVision(data.professional_vision || '');
          setPersonalVision(data.personal_vision || '');
          setIdealDay(data.ideal_day || '');
          setHealthVision(data.health_vision || '');
          setRelationshipsVision(data.relationships_vision || '');
          setProfessionalFrequency(data.professional_vision_frequency || 'monthly');
          setPersonalFrequency(data.personal_vision_frequency || 'monthly');
          setIdealDayFrequency(data.ideal_day_frequency || 'monthly');
          setHealthFrequency(data.health_vision_frequency || 'monthly');
          setRelationshipsFrequency(data.relationships_vision_frequency || 'monthly');
          setBucketListFrequency(data.bucket_list_frequency || 'monthly');
          setUserTimeZone(data.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone);
          
          if (data.professional_vision_next_review) {
            setProfessionalNextReview(new Date(data.professional_vision_next_review));
          } else {
            setProfessionalNextReview(addMonths(new Date(), 1));
          }
          
          if (data.personal_vision_next_review) {
            setPersonalNextReview(new Date(data.personal_vision_next_review));
          } else {
            setPersonalNextReview(addMonths(new Date(), 1));
          }
          
          if (data.ideal_day_next_review) {
            setIdealDayNextReview(new Date(data.ideal_day_next_review));
          } else {
            setIdealDayNextReview(addMonths(new Date(), 1));
          }
          
          if (data.health_vision_next_review) {
            setHealthNextReview(new Date(data.health_vision_next_review));
          } else {
            setHealthNextReview(addMonths(new Date(), 1));
          }
          
          if (data.relationships_vision_next_review) {
            setRelationshipsNextReview(new Date(data.relationships_vision_next_review));
          } else {
            setRelationshipsNextReview(addMonths(new Date(), 1));
          }
          
          if (data.bucket_list_next_review) {
            setBucketListNextReview(new Date(data.bucket_list_next_review));
          } else {
            setBucketListNextReview(addMonths(new Date(), 1));
          }
        }
        
        // Fetch bucket list items
        const { data: bucketItems, error: bucketError } = await supabase
          .from('bucket_list_items')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });

        if (bucketError) {
          console.error('Error fetching bucket list items:', bucketError);
        } else {
          setBucketListItems(bucketItems || []);
        }
      } catch (err: any) {
        console.error('Error fetching vision data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchVisionData();
    }
  }, [open]);

  const calculateNextReviewDate = (frequency: string): Date => {
    const now = new Date();
    
    switch (frequency) {
      case 'weekly':
        return addWeeks(now, 1);
      case 'monthly':
        return addMonths(now, 1);
      case 'quarterly':
        return addMonths(now, 3);
      default:
        return addMonths(now, 1);
    }
  };

  const handleSave = async (type: 'professional' | 'personal' | 'idealDay' | 'health' | 'relationships') => {
    try {
      setIsSaving(true);
      setError('');
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const updates: Record<string, any> = {};
      
      if (type === 'professional') {
        // Sanitize HTML content before saving
        const sanitizedVision = DOMPurify.sanitize(professionalVision);
        updates.professional_vision = sanitizedVision;
        updates.professional_vision_frequency = professionalFrequency;
        
        // Calculate next review date based on frequency
        const nextReview = calculateNextReviewDate(professionalFrequency);
        
        updates.professional_vision_next_review = nextReview.toISOString();
        setProfessionalNextReview(nextReview);
        setEditingProfessional(false);
        // Update state with sanitized content
        setProfessionalVision(sanitizedVision);
      } else if (type === 'personal') {
        // Sanitize HTML content before saving
        const sanitizedVision = DOMPurify.sanitize(personalVision);
        updates.personal_vision = sanitizedVision;
        updates.personal_vision_frequency = personalFrequency;
        
        // Calculate next review date based on frequency
        const nextReview = calculateNextReviewDate(personalFrequency);
        
        updates.personal_vision_next_review = nextReview.toISOString();
        setPersonalNextReview(nextReview);
        setEditingPersonal(false);
        // Update state with sanitized content
        setPersonalVision(sanitizedVision);
      } else if (type === 'idealDay') {
        // Sanitize HTML content before saving
        const sanitizedVision = DOMPurify.sanitize(idealDay);
        updates.ideal_day = sanitizedVision;
        updates.ideal_day_frequency = idealDayFrequency;
        
        // Calculate next review date based on frequency
        const nextReview = calculateNextReviewDate(idealDayFrequency);
        
        updates.ideal_day_next_review = nextReview.toISOString();
        setIdealDayNextReview(nextReview);
        setEditingIdealDay(false);
        // Update state with sanitized content
        setIdealDay(sanitizedVision);
      } else if (type === 'health') {
        // Sanitize HTML content before saving
        const sanitizedVision = DOMPurify.sanitize(healthVision);
        updates.health_vision = sanitizedVision;
        updates.health_vision_frequency = healthFrequency;
        
        // Calculate next review date based on frequency
        const nextReview = calculateNextReviewDate(healthFrequency);
        
        updates.health_vision_next_review = nextReview.toISOString();
        setHealthNextReview(nextReview);
        setEditingHealth(false);
        // Update state with sanitized content
        setHealthVision(sanitizedVision);
      } else if (type === 'relationships') {
        // Sanitize HTML content before saving
        const sanitizedVision = DOMPurify.sanitize(relationshipsVision);
        updates.relationships_vision = sanitizedVision;
        updates.relationships_vision_frequency = relationshipsFrequency;
        
        // Calculate next review date based on frequency
        const nextReview = calculateNextReviewDate(relationshipsFrequency);
        
        updates.relationships_vision_next_review = nextReview.toISOString();
        setRelationshipsNextReview(nextReview);
        setEditingRelationships(false);
        // Update state with sanitized content
        setRelationshipsVision(sanitizedVision);
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', session.user.id);

      if (error) throw error;

      // Record this vision update in score_history
      await supabase.from('score_history').insert({
        user_id: session.user.id,
        points: 10,
        type: `${type}_vision_update`,
        description: `Updated ${type} vision document`
      });

      setSuccessMessage(`${type.charAt(0).toUpperCase() + type.slice(1)} vision saved successfully!`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Error saving vision:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReviewComplete = async (type: 'professional' | 'personal' | 'idealDay' | 'health' | 'relationships') => {
    try {
      setIsSaving(true);
      setError('');
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const updates: Record<string, any> = {};
      
      // Calculate next review date based on frequency
      if (type === 'professional') {
        const nextReview = calculateNextReviewDate(professionalFrequency);
        updates.professional_vision_next_review = nextReview.toISOString();
        setProfessionalNextReview(nextReview);
      } else if (type === 'personal') {
        const nextReview = calculateNextReviewDate(personalFrequency);
        updates.personal_vision_next_review = nextReview.toISOString();
        setPersonalNextReview(nextReview);
      } else if (type === 'idealDay') {
        const nextReview = calculateNextReviewDate(idealDayFrequency);
        updates.ideal_day_next_review = nextReview.toISOString();
        setIdealDayNextReview(nextReview);
      } else if (type === 'health') {
        const nextReview = calculateNextReviewDate(healthFrequency);
        updates.health_vision_next_review = nextReview.toISOString();
        setHealthNextReview(nextReview);
      } else if (type === 'relationships') {
        const nextReview = calculateNextReviewDate(relationshipsFrequency);
        updates.relationships_vision_next_review = nextReview.toISOString();
        setRelationshipsNextReview(nextReview);
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', session.user.id);

      if (error) throw error;

      // Record this vision review in score_history
      await supabase.from('score_history').insert({
        user_id: session.user.id,
        points: 5,
        type: `${type}_vision_review`,
        description: `Reviewed ${type} vision document`
      });

      // Record this in growth_days to track consistency
      const today = new Date().toISOString().slice(0, 10);
      await supabase.from('growth_days').upsert(
        [{
          user_id: session.user.id,
          date: today,
          vision_reviewed: true,
          updated_at: new Date().toISOString()
        }],
        { onConflict: ['user_id', 'date'] }
      );

      setSuccessMessage(`${type.charAt(0).toUpperCase() + type.slice(1)} vision reviewed! Next review scheduled.`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Error marking vision as reviewed:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBucketListReviewComplete = async () => {
    try {
      setIsSaving(true);
      setError('');
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      // Calculate next review date based on frequency
      const nextReview = calculateNextReviewDate(bucketListFrequency);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          bucket_list_next_review: nextReview.toISOString()
        })
        .eq('id', session.user.id);

      if (error) throw error;

      setBucketListNextReview(nextReview);

      // Record this bucket list review in score_history
      await supabase.from('score_history').insert({
        user_id: session.user.id,
        points: 5,
        type: 'bucket_list_review',
        description: 'Reviewed bucket list'
      });

      // Record this in growth_days to track consistency
      const today = new Date().toISOString().slice(0, 10);
      await supabase.from('growth_days').upsert(
        [{
          user_id: session.user.id,
          date: today,
          vision_reviewed: true,
          updated_at: new Date().toISOString()
        }],
        { onConflict: ['user_id', 'date'] }
      );

      setSuccessMessage('Bucket list reviewed! Next review scheduled.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Error marking bucket list as reviewed:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const addBucketListItem = async () => {
    if (!newBucketItem.trim()) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('bucket_list_items')
        .insert([{
          user_id: session.user.id,
          item: newBucketItem.trim(),
          reason: newBucketReason.trim() || null,
          status: 'in_bucket'
        }])
        .select()
        .single();

      if (error) throw error;

      setBucketListItems(prev => [...prev, data]);
      setNewBucketItem('');
      setNewBucketReason('');
    } catch (err: any) {
      console.error('Error adding bucket list item:', err);
      setError(err.message || 'Failed to add bucket list item');
    } finally {
      setIsSaving(false);
    }
    if (!newItem.trim()) return;
    
    try {
      setIsSaving(true);
      setError('');
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('bucket_list_items')
        .insert({
          user_id: session.user.id,
          item: newItem.trim(),
          reason: newReason.trim() || null,
          status: 'in_bucket'
        })
        .select()
        .single();

      if (error) throw error;

      setBucketListItems(prev => [data, ...prev]);
      setNewItem('');
      setNewReason('');
      
      setSuccessMessage('Bucket list item added successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Error adding bucket list item:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const startEditingBucketItem = (item: any) => {
    setEditingBucketItem(item.id);
    setEditingBucketText(item.item);
    setEditingBucketReason(item.reason || '');
  };

  const saveEditingBucketItem = async () => {
    if (!editingBucketText.trim() || !editingBucketItem) return;
    
    setIsSaving(true);
    setError('');
    
    try {
      const { error } = await supabase
        .from('bucket_list_items')
        .update({
          item: editingBucketText.trim(),
          reason: editingBucketReason.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingBucketItem);

      if (error) throw error;

      setBucketListItems(prev => 
        prev.map(item => 
          item.id === editingBucketItem 
            ? { ...item, item: editingBucketText.trim(), reason: editingBucketReason.trim() || null }
            : item
        )
      );
      
      setEditingBucketItem(null);
      setEditingBucketText('');
      setEditingBucketReason('');
    } catch (err: any) {
      console.error('Error updating bucket list item:', err);
      setError(err.message || 'Failed to update bucket list item');
    } finally {
      setIsSaving(false);
    }
  };

  const cancelEditingBucketItem = () => {
    setEditingBucketItem(null);
    setEditingBucketText('');
    setEditingBucketReason('');
  };

  const getFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'weekly': return 'Weekly';
      case 'monthly': return 'Monthly';
      case 'quarterly': return 'Quarterly';
      default: return 'Monthly';
    }
  };

  const isReviewDue = (nextReview: Date | null): boolean => {
    if (!nextReview) return false;
    return new Date() >= nextReview;
  };

  // Helper function to sanitize HTML for display
  const sanitizeHtml = (html: string): string => {
    return DOMPurify.sanitize(html);
  };

  // Format date with user's timezone
  const formatDate = (date: Date): string => {
    try {
      return format(date, 'MMMM d, yyyy');
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid date';
    }
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
      <button
        onClick={() => toggle(sectionKey)}
        className="w-full flex justify-between items-center px-4 py-6 text-left hover:bg-blue-50"
      >
        <div className="flex items-center gap-2 text-[#004060] font-semibold text-xl">
          <BookText className="w-6 h-6 text-[#236caf]" />
          Vision Documents
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-[#236caf] transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="p-4 sm:p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm mb-4">
              {successMessage}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-4">Loading vision documents...</div>
          ) : (
            <>
              {/* Professional Vision Sub-Accordion */}
              <div className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setProfessionalOpen(!professionalOpen)}
                  className={cn(
                    "w-full flex justify-between items-center px-4 py-3 text-left bg-gray-50 hover:bg-gray-100",
                    isReviewDue(professionalNextReview) && "border-l-4 border-yellow-400"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#004060]">Professional Vision</h3>
                    {isReviewDue(professionalNextReview) && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                        Review Due
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 text-[#236caf] transition-transform duration-200',
                      professionalOpen && 'rotate-180'
                    )}
                  />
                </button>

                {professionalOpen && (
                  <div className="p-4 space-y-3">
                    <div className="flex justify-end">
                      {!editingProfessional ? (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            onClick={() => setEditingProfessional(true)}
                            className="flex items-center gap-1 text-sm"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </Button>
                          {professionalVision && (
                            <Button 
                              variant="ghost" 
                              onClick={() => handleReviewComplete('professional')}
                              disabled={isSaving}
                              className="flex items-center gap-1 text-sm text-green-600"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Mark as Reviewed
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            onClick={() => setEditingProfessional(false)}
                            className="flex items-center gap-1 text-sm"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </Button>
                          <Button 
                            onClick={() => handleSave('professional')}
                            disabled={isSaving}
                            className="flex items-center gap-1 text-sm"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </Button>
                        </div>
                      )}
                    </div>

                    {editingProfessional ? (
                      <div className="space-y-3">
                        <div className="quill-container">
                          <ReactQuill
                            theme="snow"
                            value={professionalVision}
                            onChange={setProfessionalVision}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Describe your professional vision here..."
                            className="bg-white rounded-lg"
                            style={{ height: '200px', marginBottom: '40px' }}
                          />
                        </div>
                        <div className="flex items-center gap-2 mt-10">
                          <span className="text-sm text-gray-600">Review frequency:</span>
                          <select
                            value={professionalFrequency}
                            onChange={(e) => setProfessionalFrequency(e.target.value)}
                            className="border border-gray-300 rounded p-1 text-sm"
                          >
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {professionalVision ? (
                          <div className="bg-blue-50 p-4 rounded-lg">
                            <div 
                              className="text-gray-700 vision-content"
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(professionalVision) }}
                            />
                          </div>
                        ) : (
                          <div className="bg-gray-50 p-4 rounded-lg text-gray-500 italic">
                            No professional vision set. Click Edit to add one.
                          </div>
                        )}
                        {professionalNextReview && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>Next review: {formatDate(professionalNextReview)} ({getFrequencyLabel(professionalFrequency)})</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Personal Vision Sub-Accordion */}
              <div className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setPersonalOpen(!personalOpen)}
                  className={cn(
                    "w-full flex justify-between items-center px-4 py-3 text-left bg-gray-50 hover:bg-gray-100",
                    isReviewDue(personalNextReview) && "border-l-4 border-yellow-400"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#004060]">Personal Vision</h3>
                    {isReviewDue(personalNextReview) && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                        Review Due
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 text-[#236caf] transition-transform duration-200',
                      personalOpen && 'rotate-180'
                    )}
                  />
                </button>

                {personalOpen && (
                  <div className="p-4 space-y-3">
                    <div className="flex justify-end">
                      {!editingPersonal ? (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            onClick={() => setEditingPersonal(true)}
                            className="flex items-center gap-1 text-sm"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </Button>
                          {personalVision && (
                            <Button 
                              variant="ghost" 
                              onClick={() => handleReviewComplete('personal')}
                              disabled={isSaving}
                              className="flex items-center gap-1 text-sm text-green-600"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Mark as Reviewed
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            onClick={() => setEditingPersonal(false)}
                            className="flex items-center gap-1 text-sm"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </Button>
                          <Button 
                            onClick={() => handleSave('personal')}
                            disabled={isSaving}
                            className="flex items-center gap-1 text-sm"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </Button>
                        </div>
                      )}
                    </div>

                    {editingPersonal ? (
                      <div className="space-y-3">
                        <div className="quill-container">
                          <ReactQuill
                            theme="snow"
                            value={personalVision}
                            onChange={setPersonalVision}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Describe your personal vision here..."
                            className="bg-white rounded-lg"
                            style={{ height: '200px', marginBottom: '40px' }}
                          />
                        </div>
                        <div className="flex items-center gap-2 mt-10">
                          <span className="text-sm text-gray-600">Review frequency:</span>
                          <select
                            value={personalFrequency}
                            onChange={(e) => setPersonalFrequency(e.target.value)}
                            className="border border-gray-300 rounded p-1 text-sm"
                          >
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {personalVision ? (
                          <div className="bg-green-50 p-4 rounded-lg">
                            <div 
                              className="text-gray-700 vision-content"
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(personalVision) }}
                            />
                          </div>
                        ) : (
                          <div className="bg-gray-50 p-4 rounded-lg text-gray-500 italic">
                            No personal vision set. Click Edit to add one.
                          </div>
                        )}
                        {personalNextReview && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>Next review: {formatDate(personalNextReview)} ({getFrequencyLabel(personalFrequency)})</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Ideal Day Sub-Accordion */}
              <div className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setIdealDayOpen(!idealDayOpen)}
                  className={cn(
                    "w-full flex justify-between items-center px-4 py-3 text-left bg-gray-50 hover:bg-gray-100",
                    isReviewDue(idealDayNextReview) && "border-l-4 border-yellow-400"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#004060]">My Ideal Day</h3>
                    {isReviewDue(idealDayNextReview) && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                        Review Due
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 text-[#236caf] transition-transform duration-200',
                      idealDayOpen && 'rotate-180'
                    )}
                  />
                </button>

                {idealDayOpen && (
                  <div className="p-4 space-y-3">
                    <div className="flex justify-end">
                      {!editingIdealDay ? (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            onClick={() => setEditingIdealDay(true)}
                            className="flex items-center gap-1 text-sm"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </Button>
                          {idealDay && (
                            <Button 
                              variant="ghost" 
                              onClick={() => handleReviewComplete('idealDay')}
                              disabled={isSaving}
                              className="flex items-center gap-1 text-sm text-green-600"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Mark as Reviewed
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            onClick={() => setEditingIdealDay(false)}
                            className="flex items-center gap-1 text-sm"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </Button>
                          <Button 
                            onClick={() => handleSave('idealDay')}
                            disabled={isSaving}
                            className="flex items-center gap-1 text-sm"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </Button>
                        </div>
                      )}
                    </div>

                    {editingIdealDay ? (
                      <div className="space-y-3">
                        <div className="quill-container">
                          <ReactQuill
                            theme="snow"
                            value={idealDay}
                            onChange={setIdealDay}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Describe your ideal day here..."
                            className="bg-white rounded-lg"
                            style={{ height: '200px', marginBottom: '40px' }}
                          />
                        </div>
                        <div className="flex items-center gap-2 mt-10">
                          <span className="text-sm text-gray-600">Review frequency:</span>
                          <select
                            value={idealDayFrequency}
                            onChange={(e) => setIdealDayFrequency(e.target.value)}
                            className="border border-gray-300 rounded p-1 text-sm"
                          >
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {idealDay ? (
                          <div className="bg-purple-50 p-4 rounded-lg">
                            <div 
                              className="text-gray-700 vision-content"
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(idealDay) }}
                            />
                          </div>
                        ) : (
                          <div className="bg-gray-50 p-4 rounded-lg text-gray-500 italic">
                            No ideal day vision set. Click Edit to add one.
                          </div>
                        )}
                        {idealDayNextReview && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>Next review: {formatDate(idealDayNextReview)} ({getFrequencyLabel(idealDayFrequency)})</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Health Vision Sub-Accordion */}
              <div className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setHealthOpen(!healthOpen)}
                  className={cn(
                    "w-full flex justify-between items-center px-4 py-3 text-left bg-gray-50 hover:bg-gray-100",
                    isReviewDue(healthNextReview) && "border-l-4 border-yellow-400"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#004060]">Health Vision</h3>
                    {isReviewDue(healthNextReview) && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                        Review Due
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 text-[#236caf] transition-transform duration-200',
                      healthOpen && 'rotate-180'
                    )}
                  />
                </button>

                {healthOpen && (
                  <div className="p-4 space-y-3">
                    <div className="flex justify-end">
                      {!editingHealth ? (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            onClick={() => setEditingHealth(true)}
                            className="flex items-center gap-1 text-sm"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </Button>
                          {healthVision && (
                            <Button 
                              variant="ghost" 
                              onClick={() => handleReviewComplete('health')}
                              disabled={isSaving}
                              className="flex items-center gap-1 text-sm text-green-600"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Mark as Reviewed
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            onClick={() => setEditingHealth(false)}
                            className="flex items-center gap-1 text-sm"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </Button>
                          <Button 
                            onClick={() => handleSave('health')}
                            disabled={isSaving}
                            className="flex items-center gap-1 text-sm"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </Button>
                        </div>
                      )}
                    </div>

                    {editingHealth ? (
                      <div className="space-y-3">
                        <div className="quill-container">
                          <ReactQuill
                            theme="snow"
                            value={healthVision}
                            onChange={setHealthVision}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Describe your health vision here..."
                            className="bg-white rounded-lg"
                            style={{ height: '200px', marginBottom: '40px' }}
                          />
                        </div>
                        <div className="flex items-center gap-2 mt-10">
                          <span className="text-sm text-gray-600">Review frequency:</span>
                          <select
                            value={healthFrequency}
                            onChange={(e) => setHealthFrequency(e.target.value)}
                            className="border border-gray-300 rounded p-1 text-sm"
                          >
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {healthVision ? (
                          <div className="bg-green-50 p-4 rounded-lg">
                            <div 
                              className="text-gray-700 vision-content"
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(healthVision) }}
                            />
                          </div>
                        ) : (
                          <div className="bg-gray-50 p-4 rounded-lg text-gray-500 italic">
                            No health vision set. Click Edit to add one.
                          </div>
                        )}
                        {healthNextReview && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>Next review: {formatDate(healthNextReview)} ({getFrequencyLabel(healthFrequency)})</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Relationships Vision Sub-Accordion */}
              <div className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setRelationshipsOpen(!relationshipsOpen)}
                  className={cn(
                    "w-full flex justify-between items-center px-4 py-3 text-left bg-gray-50 hover:bg-gray-100",
                    isReviewDue(relationshipsNextReview) && "border-l-4 border-yellow-400"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#004060]">Relationships Vision</h3>
                    {isReviewDue(relationshipsNextReview) && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                        Review Due
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 text-[#236caf] transition-transform duration-200',
                      relationshipsOpen && 'rotate-180'
                    )}
                  />
                </button>

                {relationshipsOpen && (
                  <div className="p-4 space-y-3">
                    <div className="flex justify-end">
                      {!editingRelationships ? (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            onClick={() => setEditingRelationships(true)}
                            className="flex items-center gap-1 text-sm"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </Button>
                          {relationshipsVision && (
                            <Button 
                              variant="ghost" 
                              onClick={() => handleReviewComplete('relationships')}
                              disabled={isSaving}
                              className="flex items-center gap-1 text-sm text-green-600"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Mark as Reviewed
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            onClick={() => setEditingRelationships(false)}
                            className="flex items-center gap-1 text-sm"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </Button>
                          <Button 
                            onClick={() => handleSave('relationships')}
                            disabled={isSaving}
                            className="flex items-center gap-1 text-sm"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </Button>
                        </div>
                      )}
                    </div>

                    {editingRelationships ? (
                      <div className="space-y-3">
                        <div className="quill-container">
                          <ReactQuill
                            theme="snow"
                            value={relationshipsVision}
                            onChange={setRelationshipsVision}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Describe your relationships vision here..."
                            className="bg-white rounded-lg"
                            style={{ height: '200px', marginBottom: '40px' }}
                          />
                        </div>
                        <div className="flex items-center gap-2 mt-10">
                          <span className="text-sm text-gray-600">Review frequency:</span>
                          <select
                            value={relationshipsFrequency}
                            onChange={(e) => setRelationshipsFrequency(e.target.value)}
                            className="border border-gray-300 rounded p-1 text-sm"
                          >
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="quarterly">Quarterly</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {relationshipsVision ? (
                          <div className="bg-pink-50 p-4 rounded-lg">
                            <div 
                              className="text-gray-700 vision-content"
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(relationshipsVision) }}
                            />
                          </div>
                        ) : (
                          <div className="bg-gray-50 p-4 rounded-lg text-gray-500 italic">
                            No relationships vision set. Click Edit to add one.
                          </div>
                        )}
                        {relationshipsNextReview && (
                          <div className="flex items-center gap-1 mt-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>Next review: {formatDate(relationshipsNextReview)} ({getFrequencyLabel(relationshipsFrequency)})</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Bucket List Sub-Accordion */}
              <div className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setBucketListOpen(!bucketListOpen)}
                  className={cn(
                    "w-full flex justify-between items-center px-4 py-3 text-left bg-gray-50 hover:bg-gray-100",
                    isReviewDue(bucketListNextReview) && "border-l-4 border-yellow-400"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-[#004060]">Bucket List</h3>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                      {bucketListItems.length} items
                    </span>
                    {isReviewDue(bucketListNextReview) && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                        Review Due
                      </span>
                    )}
                  </div>
                  <ChevronDown
                    className={cn(
                      'w-4 h-4 text-[#236caf] transition-transform duration-200',
                      bucketListOpen && 'rotate-180'
                    )}
                  />
                </button>

                {bucketListOpen && (
                  <div className="p-4 space-y-4">
                    {/* Bucket List Items */}
                    <div className="space-y-3">
                      {bucketListItems.length === 0 ? (
                        <div className="text-center py-4 text-gray-500">
                          <p>Your bucket list is empty.</p>
                          <p className="text-sm">Add your first item below!</p>
                        </div>
                      ) : (
                        bucketListItems.map((item) => (
                          <div key={item.id} className="border rounded-lg p-4 space-y-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                {editingBucketItem === item.id ? (
                                  <div className="space-y-2">
                                    <input
                                      type="text"
                                      value={editingBucketText}
                                      onChange={(e) => setEditingBucketText(e.target.value)}
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
                                    />
                                    <textarea
                                      value={editingBucketReason}
                                      onChange={(e) => setEditingBucketReason(e.target.value)}
                                      placeholder="Why is this important to you? (optional)"
                                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent resize-none h-16"
                                    />
                                    <div className="flex gap-2">
                                      <Button
                                        onClick={saveEditingBucketItem}
                                        disabled={!editingBucketText.trim() || isSaving}
                                        className="text-sm px-3 py-1"
                                      >
                                        Save
                                      </Button>
                                      <Button
                                        onClick={cancelEditingBucketItem}
                                        variant="outline"
                                        className="text-sm px-3 py-1"
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <h5 className="font-medium text-gray-800">{item.item}</h5>
                                    {item.reason && (
                                      <p className="text-sm text-gray-600 mt-1">{item.reason}</p>
                                    )}
                                  </>
                                )}
                              </div>
                              {editingBucketItem !== item.id && (
                                <div className="flex items-center gap-2">
                                  <select
                                    value={item.status}
                                    onChange={(e) => updateBucketListStatus(item.id, e.target.value)}
                                    className="text-xs border border-gray-300 rounded px-2 py-1"
                                  >
                                    <option value="in_bucket">In the Bucket</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="completed">Completed</option>
                                  </select>
                                  <button
                                    onClick={() => startEditingBucketItem(item)}
                                    className="text-gray-500 hover:text-[#236caf] p-1"
                                    title="Edit item"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteBucketListItem(item.id)}
                                    className="text-gray-500 hover:text-red-500 p-1"
                                    title="Delete item"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Status Badge */}
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                "text-xs px-2 py-1 rounded-full",
                                item.status === 'in_bucket' && "bg-gray-100 text-gray-700",
                                item.status === 'in_progress' && "bg-yellow-100 text-yellow-700",
                                item.status === 'completed' && "bg-green-100 text-green-700"
                              )}>
                                {item.status === 'in_bucket' && 'In the Bucket'}
                                {item.status === 'in_progress' && 'In Progress'}
                                {item.status === 'completed' && 'Completed'}
                              </span>
                              {item.completed_at && (
                                <span className="text-xs text-gray-500">
                                  Completed {formatDate(item.completed_at)}
                                </span>
                              )}
                            </div>

                            {/* Completion Story */}
                            {item.status === 'completed' && item.completion_story && (
                              <div className="bg-green-50 p-3 rounded-lg">
                                <h6 className="font-medium text-green-800 mb-2">Completion Story:</h6>
                                <p className="text-green-700 text-sm whitespace-pre-wrap">{item.completion_story}</p>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                    {/* Add New Item Form */}
                    <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                      <h4 className="font-medium text-[#004060]">Add New Item</h4>
                      <input
                        type="text"
                        value={newBucketItem}
                        onChange={(e) => setNewBucketItem(e.target.value)}
                        placeholder="What do you want to add to your bucket list?"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent"
                      />
                      <textarea
                        value={newBucketReason}
                        onChange={(e) => setNewBucketReason(e.target.value)}
                        placeholder="Why is this important to you? (optional)"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent resize-none h-20"
                      />
                      <Button
                        onClick={addBucketListItem}
                        disabled={!newBucketItem.trim() || isSaving}
                        className="w-full"
                      >
                        {isSaving ? 'Adding...' : 'Add to Bucket List'}
                      </Button>
                    </div>

                    {/* Review Settings */}
                    <div className="border-t pt-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            Review frequency: {getFrequencyLabel(bucketListFrequency)}
                          </span>
                        </div>
                        <select
                          value={bucketListFrequency}
                          onChange={(e) => updateBucketListFrequency(e.target.value)}
                          className="border border-gray-300 rounded p-1 text-sm"
                        >
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                        </select>
                      </div>
                      {bucketListNextReview && (
                        <div className="flex items-center gap-1 mt-2 text-sm text-gray-600">
                          <span>Next review: {formatDate(bucketListNextReview)}</span>
                        </div>
                      )}
                      <Button
                        onClick={handleBucketListReviewComplete}
                        disabled={isSaving}
                        className="w-full mt-3 text-sm"
                        variant="outline"
                      >
                        Mark Bucket List as Reviewed
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="text-sm text-gray-600 mt-4 p-4 bg-blue-50 rounded-lg">
                <p>Your vision documents help you stay focused on your long-term goals and values. Review them regularly to keep them fresh in your mind and update them as your goals evolve.</p>
                <p className="mt-2">Tip: Use your Gold File to help create and refine your vision documents.</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Completion Story Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-[#004060] mb-4">
              Congratulations! 🎉
            </h3>
            <p className="text-gray-600 mb-4">
              You've completed "{completingItem?.item}". Tell us about your experience:
            </p>
            <textarea
              value={completionStory}
              onChange={(e) => setCompletionStory(e.target.value)}
              placeholder="Share your story... What happened? How did it feel? What did you learn?"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent resize-none h-32"
            />
            <div className="flex gap-2 mt-4">
              <Button
                onClick={saveCompletionStory}
                disabled={isSaving}
                className="flex-1"
              >
                {isSaving ? 'Saving...' : 'Save Story'}
              </Button>
              <Button
                onClick={() => {
                  setShowCompletionModal(false);
                  setCompletingItem(null);
                  setCompletionStory('');
                }}
                variant="outline"
                className="flex-1"
              >
                Skip for Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}